import * as React from 'react';
import { Component, Fragment } from 'react';
import { computed, toJS, action, observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import styled, { withTheme } from 'styled-components';
import get from 'lodash/get';
import isString from 'lodash/isString';
import { propStyles } from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import Disclaimers from './Disclaimers';
import Translate, { translate } from './Translate';
import ChartBarChart from './ChartBarChart';
import ChartLineChart from './ChartLineChart';
import ChartPeriodSelector from './ChartPeriodSelector';
import ChartDateSelector from './ChartDateSelector';
import performanceCheck from '../utils/performanceCheck';
import getDataPoint from '../utils/getDataPoint';
import { getDisclaimers } from '../utils/disclaimers';
import getDateDifference from '../utils/getDateDifference';
import * as isWithinRange from 'date-fns/is_within_range';
import * as isBefore from 'date-fns/is_before';
import * as isAfter from 'date-fns/is_after';
import { DataStore, TranslationStore } from '../stores';
import { ShareClass } from '../models/entity';
import { Chart as ChartComponent } from '../models/configuration/chart/Chart';
import { ChartConfig } from '../models/configuration/chart/ChartConfig';
import { XAxis } from '../models/configuration/chart/XAxis';
import { YAxis } from '../models/configuration/chart/YAxis';
import { Series } from '../models/configuration/chart/Series';
import { AsAtDate } from '../models/configuration/AsAtDate';
import { DataLocator } from '../models/configuration/DataLocator';
import {
    PeriodSelector,
    PeriodSelectorOptions,
} from '../models/configuration/chart/PeriodSelector';
import { DateSelector } from '../models/configuration/chart/DateSelector';
import {
    isNullOrUndefined,
    isNullOrEmpty,
} from '@kurtosys/ksys-app-template/dist/utils/typeChecks';
import getQueryResults from '../utils/query/getQueryResults';
import { IQueryContext } from '@kurtosys/ksys-app-template/dist/models/query';
import { isArrayLike } from '@kurtosys/ksys-app-template/dist/utils/isArrayLike';

const ChartWrapper = styled<any, any>('div')`
    ${propStyles};
`;

ChartWrapper.defaultProps = {
    marginY: 'md',
    overflow: 'hidden',
};

const ChartHeading = styled<any, any>('h2')`
    ${propStyles};
`;

const ChartContainer = styled<any, any>('div')`
    ${propStyles};
`;

ChartHeading.defaultProps = {
    color: 'body',
    fontFamily: 'heading',
    fontWeight: 'bold',
    fontSize: 'md',
};

const ChartHeader = styled<any, any>('div')`
    ${propStyles};
`;

ChartHeader.defaultProps = {
    display: 'flex',
    flexDirection: ['column', 'row'],
    justifyContent: ['center', 'space-between'],
    fontSize: 'xs',
};

const ChartControls = styled<any, any>('div')`
    ${propStyles};
`;

ChartControls.defaultProps = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: ['center', 'space-between'],
};

const ChartDatesWrapper = styled<any, any>('div')`
    ${propStyles};
`;

ChartDatesWrapper.defaultProps = {
    display: 'flex',
};

const AsAtDate = styled<any, any>('div')`
    ${propStyles};
`;

AsAtDate.defaultProps = {
    flex: '1 0 auto',
    fontSize: 'xs',
    textAlign: ['center', 'right'],
    marginX: 'sm',
    marginY: 'md',
};

interface ChartProps {
    theme?: any;
    dataStore: DataStore;
    translationStore: TranslationStore;
    chart: ChartComponent;
    shareClass: ShareClass;
    queryContext: IQueryContext;
    showHeading?: boolean;
    isMobile?: boolean;
}

interface DataSetValue {
    label?: string;
    date?: string;
    value?: number | string;
}

export interface PeriodSelectorOptionWithDates extends PeriodSelectorOptions {
    startDate: string;
    endDate: string;
    hasDataForPeriod: boolean;
}

interface PeriodSelectorExtendedOptions extends PeriodSelector {
    options: Array<PeriodSelectorOptionWithDates>;
}

interface AsAtDateValue {
    label: string;
    value: string;
}

@inject('dataStore')
@inject('translationStore')
@observer
class Chart extends Component<ChartProps> {
    @observable startDate: string;
    @observable endDate: string;

    constructor(props) {
        super(props);

        if (this.hasPeriodSelection) {
            const { options = [] } = this.periodSelector;
            const selectedPeriod = options.find(o => o.selected);

            selectedPeriod
                ? this.setPeriod(selectedPeriod)
                : this.setDefaultPeriod();
        }
    }

    @computed
    get locale(): string {
        return this.props.dataStore.culture;
    }

    @computed
    get fundCurrency(): string {
        return get(
            this.props.shareClass,
            `properties_pub.${this.props.dataStore.config
                .fundCurrencyProperty || 'ccy'}.value`
        );
    }

    // Chart data
    @computed
    get dataSet(): Array<DataSetValue> {
        const { dataStore, translationStore } = this.props;
        const {
            data,
            config: { dataMap = [], series },
            hideEmptyDataPoints = false,
            inceptionInjection,
        } = this.props.chart;
        let dataSet = [];

        if (isArrayLike(data)) {
            dataSet = (data as []).map(d => {
                this.sortDataForChartControls(d);
                return getDataPoint({
                    dataStore,
                    dataConfig: d,
                    shareClass: this.props.shareClass,
                    locale: this.locale,
                    fundCurrency: this.fundCurrency,
                    defaultNoDataPlaceholder: 0,
                    hasPerformance: this.hasPerformance,
                });
            });
        } else {
            // checks and sorts dates when using chart controls
            this.sortDataForChartControls(data);
            dataSet = getDataPoint({
                dataStore,
                dataConfig: data as DataLocator,
                shareClass: this.props.shareClass,
                locale: this.locale,
                fundCurrency: this.fundCurrency,
                defaultNoDataPlaceholder: 0,
                hasPerformance: this.hasPerformance,
            });
        }

        // Convert possible string "number" values to float for recharts to handle correctly
        for (const seriesItem of series) {
            const dataKey = seriesItem.dataKey;
            // tslint:disable-next-line:prefer-for-of
            for (let index = 0; index < dataSet.length; index++) {
                const element = dataSet[index];
                if (!isNullOrUndefined(element[dataKey])) {
                    // we are dealing with mobx observables and need to deref the dataset
                    const dataSetItem = { ...element };
                    dataSetItem[dataKey] = parseFloat(element[dataKey]);
                    // reassign to dataSet to replace ref to mobx observable.
                    dataSet[index] = dataSetItem;
                }
            }
        }

        if (inceptionInjection) {
            const { property, dateKey = 'date' } = inceptionInjection;
            if (property) {
                const inceptionDate = getDataPoint({
                    dataStore,
                    dataConfig: inceptionInjection.property,
                    shareClass: this.props.shareClass,
                    locale: this.locale,
                    fundCurrency: this.fundCurrency,
                    defaultNoDataPlaceholder: 0,
                    hasPerformance: this.hasPerformance,
                });
                if (inceptionDate) {
                    const row = {
                        [dateKey]: inceptionDate,
                    };
                    for (const seriesItem of series) {
                        const dataKey = seriesItem.dataKey;
                        row[dataKey] = 0;
                    }
                    dataSet.unshift(row);
                }
            }
        }

        if (dataMap.length > 0) {
            dataSet = dataMap.map(map => {
                const mappedData = {};

                for (const property in map) {
                    if (map[property]) {
                        mappedData[property] = translate(
                            translationStore,
                            map[property],
                            dataSet
                        );
                    }
                }

                return mappedData;
            });
        }

        if (this.xAxis && this.xAxis.ticksQuery) {
            dataSet = this.getAxisTicks(this.xAxis, dataSet);
        }

        if (this.yAxis && this.yAxis.ticksQuery) {
            dataSet = this.getAxisTicks(this.yAxis, dataSet);
        }

        if (hideEmptyDataPoints && !isNullOrEmpty(dataSet)) {
            return dataSet.filter(point => this.dataPointHasData(point));
        }
        return dataSet;
    }

    // Chart controls needs to check that the current date is between the start and end dates.
    // Therefore to avoid date fns errors, we need to ensure that the start date is also before the end date.
    sortDataForChartControls(data: any) {
        const { chartControls } = this.props.chart;
        if (chartControls) {
            data.sort = {
                type: 'date',
                key: this.dateProperty,
                direction: 'ASC',
            };
        }
    }

    getAxisTicks = (
        axis: XAxis | YAxis,
        data: Array<DataSetValue>
    ): Array<DataSetValue> => {
        const { queryContext } = this.props;
        if (data && isArrayLike(data) && axis.ticksQuery) {
            // mobx.toJS was not sufficiently preparing the data for the map to occur
            const results = data.slice().map((row, idx) => {
                // spread ticksQuery to deref from config store since we are mutating the object further down
                let query = { ...axis.ticksQuery };
                if (query.subQueries) {
                    // Amend the index for each subQuery
                    query.subQueries = query.subQueries.map(subQuery => {
                        return { ...subQuery, index: idx };
                    });
                } else {
                    // else amend for the query
                    query = { ...query, index: idx };
                }
                return {
                    ...row,
                    ...getQueryResults(
                        queryContext,
                        {
                            [axis.dataKey]: query,
                        },
                        this.locale
                    ),
                };
            });
            return results;
        }
    };

    dataPointHasData(dataPoint: any) {
        const { series = [] } = this;
        return (
            series.filter(item => !isNullOrEmpty(dataPoint[item.dataKey]))
                .length > 0
        );
    }

    // Filtered chart data
    @computed
    get data(): Array<DataSetValue> {
        if (this.hasPeriodSelection) {
            const dataSet = this.dataSet.filter(data => {
                const currentDate = get(data, this.dateProperty);
                return isWithinRange(currentDate, this.startDate, this.endDate);
            });

            return dataSet;
        }

        return this.dataSet;
    }

    // Date property in chart data
    @computed
    get dateProperty(): string {
        const { chartControls = {} } = this.props.chart;
        const { dateProperty = 'date' } = chartControls;
        return dateProperty;
    }

    // Chart dates for date selectors
    @computed
    get chartDatesStart(): Array<string> {
        return this.dataSet
            .map(data => {
                const date = get(data, this.dateProperty);
                if (isBefore(date, this.endDate)) {
                    return date;
                }
            })
            .filter(d => d !== undefined);
    }

    // Chart dates for date selectors
    @computed
    get chartDatesEnd(): Array<string> {
        return this.dataSet
            .map(data => {
                const date = get(data, this.dateProperty);
                if (isAfter(date, this.startDate)) {
                    return date;
                }
            })
            .filter(d => d !== undefined);
    }

    // Chart controls/date selections
    @computed
    get periodSelector(): PeriodSelectorExtendedOptions {
        const { chartControls = {} } = this.props.chart;
        const { periodSelector = {} } = chartControls;
        const { options = [] } = periodSelector as PeriodSelector;

        return {
            ...periodSelector,
            options: options.map(option => {
                const dateRange = this.getPeriodStartEndDates(option);
                return {
                    ...option,
                    ...dateRange,
                };
            }),
        };
    }

    @computed
    get dateSelector(): DateSelector {
        const { chartControls = {} } = this.props.chart;
        const { dateSelector } = chartControls;

        if (dateSelector) {
            const defaultDateSelector = {
                startDateLabel: 'Start date',
                endDateLabel: 'End date',
            };

            return {
                ...defaultDateSelector,
                ...dateSelector,
            };
        }

        return {};
    }

    @computed
    get hasDateSelector(): boolean {
        return Object.keys(this.dateSelector).length > 0;
    }

    @computed
    get hasPeriodSelector(): boolean {
        return this.periodSelector && this.periodSelector.options.length > 0;
    }

    @computed
    get hasPeriodSelection(): boolean {
        return this.hasDateSelector || this.hasPeriodSelector;
    }

    @action
    setPeriod = (periodOption: PeriodSelectorOptionWithDates) => {
        this.startDate = periodOption.startDate;
        this.endDate = periodOption.endDate;
    };

    @action
    setDefaultPeriod = () => {
        this.startDate = get(this.dataSet[0], this.dateProperty);
        this.endDate = get(
            this.dataSet[this.dataSet.length - 1],
            this.dateProperty
        );
    };

    @action
    setStartDate = (date: string) => {
        this.startDate = date;
    };

    @action
    setEndDate = (date: string) => {
        this.endDate = date;
    };

    // Chart as at date
    @computed
    get asAtDate(): AsAtDateValue {
        const { dataStore } = this.props;
        const { asAtDate = {} } = this.props.chart;
        const { label, data, format } = asAtDate as AsAtDate;
        const value = getDataPoint({
            dataStore,
            dataConfig: { ...data, format },
            shareClass: this.props.shareClass,
            locale: this.locale,
            fundCurrency: this.fundCurrency,
            defaultNoDataPlaceholder: '-',
            hasPerformance: this.hasPerformance,
        });

        if (this.props.chart.asAtDate) {
            return {
                label,
                value,
            };
        }

        return null;
    }

    // Chart display
    @computed
    get hasPerformance(): boolean {
        if (this.props.chart.performanceCheck) {
            const { dataStore, shareClass } = this.props;
            const { config } = dataStore;
            return performanceCheck({
                dataStore,
                config,
                shareClass,
            });
        }

        return false;
    }

    @computed
    get displayChart(): boolean {
        if (this.props.chart.performanceCheck) {
            return this.data.length > 0 && this.hasPerformance;
        }

        return this.data.length > 0;
    }

    // Chart disclaimers
    @computed
    get chartDisclaimers(): Array<string> {
        return this.disclaimerByType(this.props.chart.disclaimerTypes);
    }

    @computed
    get noDataDisclaimers(): Array<string> {
        return this.disclaimerByType(this.props.chart.noDataDisclaimerTypes);
    }

    @computed
    get performanceCheckDisclaimers(): Array<string> {
        return this.disclaimerByType(
            this.props.chart.performanceCheckDisclaimerTypes
        );
    }

    @action
    disclaimerByType(disclaimerTypes) {
        const { disclaimers = {} } = this.props.shareClass;
        return getDisclaimers(
            this.props.dataStore,
            disclaimerTypes,
            disclaimers
        );
    }

    @computed
    get displayDisclaimers(): Array<string> {
        if (this.props.chart.performanceCheck && !this.hasPerformance) {
            return this.performanceCheckDisclaimers;
        }

        return this.noDataDisclaimers;
    }

    // Chart config
    @computed
    get config(): ChartConfig {
        return this.props.chart.config || {};
    }

    @computed
    get mobileConfig(): ChartConfig {
        return this.props.chart.mobileConfig || {};
    }

    @computed
    get xAxis(): XAxis {
        const { xAxis } = this.config;

        if (this.props.isMobile) {
            const { xAxis: xAxisMobile } = this.mobileConfig;
            return toJS({ ...xAxis, ...xAxisMobile });
        }

        return toJS(xAxis);
    }

    @computed
    get yAxis(): YAxis {
        const { yAxis } = this.config;

        if (this.props.isMobile) {
            const { yAxis: yAxisMobile } = this.mobileConfig;
            return toJS({ ...yAxis, ...yAxisMobile });
        }

        return toJS(yAxis);
    }

    @computed
    get series(): Array<Series> {
        const { series = [] } = this.config;

        return toJS(series);
    }

    @computed
    get chartHasData(): boolean {
        const { seriesDataKeys = [] } = this;
        return (
            seriesDataKeys.filter(dataKey => this.seriesHasData(dataKey))
                .length > 0
        );
    }

    @computed
    get seriesDataKeys() {
        const { series, showBlankSeries = false } = this;
        if (
            !showBlankSeries &&
            !isNullOrEmpty(series) &&
            Array.isArray(series)
        ) {
            return series
                .filter(s => this.seriesHasData(s.dataKey))
                .map(s => s.dataKey);
        }
        return series.map(s => s.dataKey);
    }

    seriesHasData(key: string) {
        const { data } = this;
        if (!isNullOrEmpty(data)) {
            if (Array.isArray(data)) {
                return (
                    data.filter(item => !isNullOrEmpty(item[key])).length > 0
                );
            }
            return !isNullOrEmpty(data[key]);
        }
        return false;
    }

    @computed
    get showBlankSeries(): boolean {
        const { showBlankSeries = false } = this.config;
        return showBlankSeries;
    }

    @computed
    get minHeight(): number {
        const { barChart, series } = this.config;
        const { isMobile } = this.props;
        if (isMobile) {
            let minHeight = 400;
            if (barChart) {
                const { barSize, barGap, barCategoryGap } = barChart;
                if (barSize && barGap && barCategoryGap) {
                    const dataCount = this.data.length;
                    const seriesCount = series.length;

                    const barSizes = dataCount * seriesCount * barSize;
                    const barGaps = dataCount * (seriesCount - 1) * barGap;
                    const categoryGaps = dataCount * barCategoryGap;

                    minHeight = barSizes + barGaps + categoryGaps;
                }
            }
            return minHeight;
        }
    }

    getPeriodStartEndDates = (periodOption: PeriodSelectorOptions) => {
        const { period, value } = periodOption;
        let startDate = get(this.dataSet[0], this.dateProperty);
        let endDate = get(
            this.dataSet[this.dataSet.length - 1],
            this.dateProperty
        );

        if (value) {
            const dataSet = this.dataSet.filter(data => {
                const currentDate = get(data, this.dateProperty);
                const dateDiff = getDateDifference(
                    endDate,
                    currentDate,
                    period
                );
                return dateDiff <= value;
            });

            startDate = get(dataSet[0], this.dateProperty);
            endDate = get(dataSet[dataSet.length - 1], this.dateProperty);

            const dateDiffForPeriod = getDateDifference(
                startDate,
                endDate,
                period
            );

            return {
                startDate,
                endDate,
                hasDataForPeriod: dateDiffForPeriod >= value,
            };
        }

        return {
            startDate,
            endDate,
            hasDataForPeriod: true,
        };
    };

    public getThemeColor = (color: string) => {
        return get(this.props.theme, `color.${color}`, color);
    };

    public getLabelName = label => {
        const { translationStore } = this.props;
        if (isString(label)) {
            return {
                value: translate(translationStore, label),
            };
        }

        const labelValue = get(label, 'value', label);
        const translatedLabelValue = translate(translationStore, labelValue);

        return {
            ...label,
            value: translatedLabelValue,
        };
    };

    public getSeriesName = (series: Series) => {
        const { queryContext, translationStore } = this.props;
        let seriesName: string | undefined;

        if (series.nameQuery) {
            const data = getQueryResults(queryContext, {
                name: series.nameQuery,
            });
            if (data && data.name) {
                seriesName = data.name;
            }
        }

        if (!seriesName && series.nameProperty) {
            seriesName = get(
                this.props.shareClass.properties_pub,
                `${series.nameProperty}.value`,
                series.name
            );
        }

        if (!seriesName) {
            seriesName = series.name;
        }

        const translatedSeriesName = translate(translationStore, seriesName);
        return translatedSeriesName;
    };

    public render() {
        const { theme, chart, showHeading, isMobile, dataStore } = this.props;
        const {
            data,
            config,
            mobileConfig,
            xAxis,
            yAxis,
            series,
            showBlankSeries,
            getThemeColor,
            getLabelName,
            getSeriesName,
            chartDisclaimers,
            displayChart,
            displayDisclaimers,
            locale,
            fundCurrency,
            asAtDate,
            periodSelector,
            hasDateSelector,
            hasPeriodSelector,
            setPeriod,
            dateSelector,
            chartDatesStart,
            chartDatesEnd,
            startDate,
            endDate,
            setStartDate,
            setEndDate,
            minHeight,
            chartHasData,
        } = this;

        if (!chartHasData && chart.noDataHidden) {
            return null;
        }

        return (
            <ChartWrapper {...theme.ChartWrapper}>
                {showHeading && (
                    <ChartHeading {...theme.ChartHeading}>
                        <Translate>{chart.label}</Translate>
                    </ChartHeading>
                )}
                {displayChart ? (
                    <Fragment>
                        <ChartContainer {...theme.ChartContainer}>
                            <ChartHeader {...theme.ChartHeader}>
                                <ChartControls {...theme.ChartControls}>
                                    {hasPeriodSelector && (
                                        <ChartPeriodSelector
                                            options={periodSelector.options}
                                            noDataForPeriodUi={
                                                periodSelector.noDataForPeriodUi
                                            }
                                            startDate={startDate}
                                            endDate={endDate}
                                            setPeriod={setPeriod}
                                        />
                                    )}
                                    {hasDateSelector && (
                                        <ChartDatesWrapper
                                            {...theme.ChartDatesWrapper}
                                        >
                                            <ChartDateSelector
                                                label={
                                                    dateSelector.startDateLabel
                                                }
                                                value={startDate}
                                                values={chartDatesStart}
                                                selectDate={setStartDate}
                                                valueFormat={
                                                    dateSelector.format
                                                }
                                                locale={locale}
                                                dataStore={dataStore}
                                            />
                                            <ChartDateSelector
                                                label={
                                                    dateSelector.endDateLabel
                                                }
                                                value={endDate}
                                                values={chartDatesEnd}
                                                selectDate={setEndDate}
                                                valueFormat={
                                                    dateSelector.format
                                                }
                                                locale={locale}
                                                dataStore={dataStore}
                                            />
                                        </ChartDatesWrapper>
                                    )}
                                </ChartControls>
                                {asAtDate && (
                                    <AsAtDate {...theme.ChartAsAtDate}>
                                        <Translate>{asAtDate.label}</Translate>{' '}
                                        {asAtDate.value}
                                    </AsAtDate>
                                )}
                            </ChartHeader>
                            {chart.type === 'BarChart' && (
                                <ChartBarChart
                                    config={config}
                                    mobileConfig={mobileConfig}
                                    data={data}
                                    xAxis={isMobile ? yAxis : xAxis}
                                    yAxis={isMobile ? xAxis : yAxis}
                                    layout={
                                        isMobile ? 'vertical' : 'horizontal'
                                    }
                                    minHeight={minHeight}
                                    aspect={isMobile ? undefined : 3}
                                    series={series}
                                    showBlankSeries={showBlankSeries}
                                    getThemeColor={getThemeColor}
                                    getLabelName={getLabelName}
                                    getSeriesName={getSeriesName}
                                    locale={locale}
                                    fundCurrency={fundCurrency}
                                    isMobile={isMobile}
                                />
                            )}
                            {chart.type === 'LineChart' && (
                                <ChartLineChart
                                    config={config}
                                    data={data}
                                    xAxis={xAxis}
                                    yAxis={yAxis}
                                    series={series}
                                    getThemeColor={getThemeColor}
                                    getLabelName={getLabelName}
                                    getSeriesName={getSeriesName}
                                    locale={locale}
                                    fundCurrency={fundCurrency}
                                />
                            )}
                        </ChartContainer>
                        <Disclaimers
                            marginX="0"
                            disclaimers={chartDisclaimers}
                            theme={{
                                ...theme,
                                DisclaimersList: theme.ChartDisclaimersList,
                                DisclaimerListItem:
                                    theme.ChartDisclaimerListItem,
                                DisclaimerCharacter:
                                    theme.ChartDisclaimerCharacter,
                                DisclaimerContent: theme.ChartDisclaimerContent,
                            }}
                        />
                    </Fragment>
                ) : (
                    <Disclaimers
                        marginX="0"
                        disclaimers={displayDisclaimers}
                        theme={{
                            ...theme,
                            DisclaimersList: theme.ChartNoDataDisclaimersList,
                            DisclaimerListItem:
                                theme.ChartNoDataDisclaimerListItem,
                            DisclaimerCharacter:
                                theme.ChartNoDataDisclaimerCharacter,
                            DisclaimerContent:
                                theme.ChartNoDataDisclaimerContent,
                        }}
                    />
                )}
            </ChartWrapper>
        );
    }
}

export default withTheme(Chart) as any;



// WEBPACK FOOTER //
// ./src/Components/Chart.tsx